import { inject, Injectable } from '@angular/core';
import { LanguageService } from '@dss-libs/shared/utility/common-services';
import { LocalstorageConstants } from '@dss-libs/shared/type/common-types';
import { CHARGEBEE_SITE } from '@dss-libs/shared/utility/global-injection-tokens';
import { SubscriptionConstants } from '@dss-libs/portal/type/portal-types';
import { ClientService } from '@dss-libs/shared/data-access/clients';
import { firstValueFrom } from 'rxjs';

declare const Chargebee: any;

@Injectable({
  providedIn: 'root',
})
export class ChargebeeFacadeService {
  private readonly chargebeeSite = inject(CHARGEBEE_SITE);
  private readonly languageService = inject(LanguageService);
  private readonly clientService = inject(ClientService);

  initializeChargebee() {
    const script = document.createElement('script');
    script.src = 'https://js.chargebee.com/v2/chargebee.js';
    script.async = true;
    script.onload = () => {
      Chargebee.init({ site: this.chargebeeSite });
      this.handleCheckoutCallbacks();
    };
    document.head.appendChild(script);
  }

  fireDropInScript(planId: string | null, billingEmail: string) {
    if (!planId) return;
    setTimeout(async () => {
      const anchorElement = document.getElementById(planId);
      if (anchorElement) {
        const planElement = document.querySelector(`[data-cb-item-0="${planId}"]`);
        await this.chargebeeInstanceInit(planElement!, planId, billingEmail);
        anchorElement?.click();
      }
    });
  }

  /**
   * Initializing Chargebee Instance
   * @param planElement selected plan element
   * @param planId selected plan id
   * @param billingEmail customer's email
   */
  private async chargebeeInstanceInit(planElement: Element, planId: string | null, billingEmail: string) {
    const cbInstance = Chargebee.getInstance();
    const product = cbInstance.getProduct(planElement);
    const cart = cbInstance.getCart();
    const clientsNumber = await firstValueFrom(this.clientService.getClientActiveUsers);

    this.prefillProductData(product, planId, clientsNumber);
    this.prefillCustomFields(product, billingEmail);
    this.prefillCartData(cart);
  }

  /**
   * Setting selected plan in chargebee instance
   * @param product Chargebee Instance Product Object
   * @param planId selected plan id
   * @param clientsNumber number of clients
   */
  private prefillProductData(product: any, planId: string | null, clientsNumber: number) {
    product.items = [];
    product.items.push({ item_price_id: planId, quantity: clientsNumber });
    product.isItemsModel = true;
  }

  /**
   * Setting Custom Fields (client_id & billing_email) in Chargebee Instance
   * @param product Chargebee Instance Product Object
   * @param billingEmail customer's email
   */
  private prefillCustomFields(product: any, billingEmail: string) {
    product.data[SubscriptionConstants.SUBSCRIPTION_CLIENT_ID] = window.localStorage.getItem(
      LocalstorageConstants.CLIENT_ID
    );
    product.setCustomData({
      [SubscriptionConstants.BILLING_EMAIL]: billingEmail,
    });
  }

  /**
   * Setting Customer Data (locale specifically) in chargebee cart instance
   * @param cart Chargebee Instance Cart Object
   */
  private prefillCartData(cart: any) {
    const customer = { locale: this.languageService.getLanguage() };
    cart.setCustomer(customer);
  }

  /**
   * This is a workaround to redirect to the home page after the checkout is successful.
   * Check the documentation for more details:
   * https://www.chargebee.com/checkout-portal-docs/dropin-reference.html#chargebee-instance-object
   */
  private handleCheckoutCallbacks() {
    Chargebee.getInstance().setCheckoutCallbacks(() => {
      return {
        success: () => window.location.replace(document.location.origin + '/subscription-in-progress'),
      };
    });
  }
}
